import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, Sparkles, Image, Settings, BarChart3, 
  TrendingUp, Calendar, MessageSquare, CreditCard,
  ArrowRight, Loader2, Shield, FileText, Video,
  Heart, Eye, Crown, Building2, GraduationCap, Database, UserPlus, Search,
  Bell, BellRing, CheckCheck, Phone, Mail, MessageCircle, RefreshCw, KeyRound,
  DollarSign, Activity, Newspaper,
} from "lucide-react";
import AdminActivitySection from "../components/AdminActivitySection";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading, token } = useAuth();
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalMannerContents: 0,
    totalImageContents: 0,
    totalSponsors: 0,
    recentUsers: [],
    // Chart data
    dailySignups: [],
    categoryDistribution: [],
    contentViews: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [counselingNotifications, setCounselingNotifications] = useState([]);
  const [counselingUnread, setCounselingUnread] = useState(0);
  const [counselingLoading, setCounselingLoading] = useState(false);

  const fetchCounselingNotifications = async () => {
    if (!token) return;
    setCounselingLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/counseling`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCounselingNotifications(data.notifications || []);
        setCounselingUnread(data.unread_count || 0);
      }
    } catch {} finally { setCounselingLoading(false); }
  };

  const markAllCounselingSeen = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/counseling/mark-all-seen`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("모든 알림을 읽음 처리했습니다.");
        fetchCounselingNotifications();
      }
    } catch {
      toast.error("처리 실패");
    }
  };

  const markOneSeen = async (bookingId) => {
    try {
      await fetch(`${API_URL}/api/admin/notifications/counseling/${bookingId}/mark-seen`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCounselingNotifications();
    } catch {}
  };

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchStats();
    fetchCounselingNotifications();
    // Poll every 60s for new counseling bookings
    const poll = setInterval(() => { fetchCounselingNotifications(); }, 60000);
    return () => clearInterval(poll);
  }, [isAuthenticated, user, loading]);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      // Fetch manner class contents
      const mannerRes = await fetch(`${API_URL}/api/manner-class/contents`);
      const mannerData = await mannerRes.json();
      
      // Fetch image consulting contents
      const imageRes = await fetch(`${API_URL}/api/image-consulting/contents`);
      const imageData = await imageRes.json();
      
      // Fetch manner class sponsors
      const mannerSponsorsRes = await fetch(`${API_URL}/api/manner-class/sponsors`);
      const mannerSponsorsData = await mannerSponsorsRes.json();
      
      // Fetch image consulting sponsors
      const imageSponsorsRes = await fetch(`${API_URL}/api/image-consulting/sponsors`);
      const imageSponsorsData = await imageSponsorsRes.json();

      // Fetch users count (admin only)
      let totalUsers = 0;
      try {
        const usersRes = await fetch(`${API_URL}/api/admin/users/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          totalUsers = usersData.count || 0;
        }
      } catch (e) {
        console.log("Users count API not available");
      }

      // Fetch curriculum contents count (admin only)
      let totalCurriculumContents = 0;
      try {
        const currRes = await fetch(`${API_URL}/api/admin/curriculum/contents?limit=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (currRes.ok) {
          const currData = await currRes.json();
          totalCurriculumContents = currData.total || 0;
        }
      } catch (e) {
        console.log("Curriculum contents API not available");
      }

      // Fetch events count (admin only)
      let totalEvents = 0;
      try {
        const eventsRes = await fetch(`${API_URL}/api/admin/events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (eventsRes.ok) {
          const eventsData = await eventsRes.json();
          totalEvents = eventsData.events?.length || 0;
        }
      } catch (e) {
        console.log("Events API not available");
      }

      // Generate chart data
      const mannerContents = mannerData.contents || [];
      const imageContents = imageData.contents || [];
      
      // Daily signups (mock data for last 7 days)
      const today = new Date();
      const dailySignups = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dayName = date.toLocaleDateString('ko-KR', { weekday: 'short' });
        dailySignups.push({
          name: dayName,
          가입자: Math.floor(Math.random() * 15) + 5,
          방문자: Math.floor(Math.random() * 100) + 50,
        });
      }

      // Category distribution for manner class
      const mannerCategories = {};
      mannerContents.forEach(c => {
        const cat = c.category || 'other';
        mannerCategories[cat] = (mannerCategories[cat] || 0) + 1;
      });
      
      const categoryLabels = {
        first_impression: '첫인상',
        date: '데이트',
        dining: '식사',
        conversation: '대화',
        emotion: '감정',
        contact: '연락',
        rejection: '거절',
        fashion: '패션',
        hair: '헤어',
        makeup: '메이크업',
        procedure: '시술',
        profile: '프로필',
      };
      
      const categoryDistribution = Object.entries(mannerCategories).map(([key, value]) => ({
        name: categoryLabels[key] || key,
        value: value,
      }));

      // Content views (top 5 from both)
      const allContents = [...mannerContents, ...imageContents]
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, 6);
      
      const contentViews = allContents.map(c => ({
        name: c.title?.substring(0, 10) + '...' || '콘텐츠',
        조회수: c.views || 0,
        좋아요: c.likes || 0,
      }));
      
      setStats({
        totalUsers,
        totalMannerContents: mannerData.contents?.length || 0,
        totalImageContents: imageData.contents?.length || 0,
        totalMannerSponsors: mannerSponsorsData.sponsors?.length || 0,
        totalImageSponsors: imageSponsorsData.sponsors?.length || 0,
        totalCurriculumContents,
        totalEvents,
        dailySignups,
        categoryDistribution,
        contentViews,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== "admin") {
    return null;
  }

  const adminModules = [
    {
      title: "연애사관학교 관리",
      description: "12개 학과 커리큘럼 콘텐츠 관리",
      icon: GraduationCap,
      href: "/admin/curriculum",
      color: "bg-indigo-500",
      stats: [
        { label: "콘텐츠", value: stats.totalCurriculumContents || 0 },
      ],
    },
    {
      title: "중년싱글 뉴스 관리",
      description: "뉴스 수집·AI 선별·승인 및 키워드 설정",
      icon: Newspaper,
      href: "/admin/news",
      color: "bg-rose-600",
      stats: [
        { label: "뉴스", value: "관리" },
      ],
    },
    {
      title: "카테고리 뉴스 관리",
      description: "커뮤니티 자동 생성 뉴스 자동수집·숨기기·삭제·고정·키워드",
      icon: Newspaper,
      href: "/admin/community-news",
      color: "bg-burgundy-600",
      stats: [
        { label: "카테고리 뉴스", value: "관리" },
      ],
    },
    {
      title: "이벤트 관리",
      description: "오프라인 미팅 및 소모임 이벤트 관리",
      icon: Calendar,
      href: "/admin/events",
      color: "bg-purple-500",
      stats: [
        { label: "이벤트", value: stats.totalEvents || 0 },
      ],
      comingSoon: false,
    },
    {
      title: "연애정보회사 상담",
      description: "하이앤드·엘리트·VIP 플랜 상담 신청 관리",
      icon: Heart,
      href: "/admin/dating-agency",
      color: "bg-rose-500",
      stats: [
        { label: "신청자", value: "관리" },
      ],
      comingSoon: false,
    },
    {
      title: "매너 클래스 관리",
      description: "매너 콘텐츠 및 추천 강사/업체 관리",
      icon: Sparkles,
      href: "/admin/manner-class",
      color: "bg-burgundy-500",
      stats: [
        { label: "콘텐츠", value: stats.totalMannerContents },
        { label: "추천업체", value: stats.totalMannerSponsors || 0 },
      ],
    },
    {
      title: "이미지 컨설팅 관리",
      description: "이미지 컨설팅 콘텐츠 및 추천업체 관리",
      icon: Image,
      href: "/admin/image-consulting",
      color: "bg-pink-500",
      stats: [
        { label: "콘텐츠", value: stats.totalImageContents },
        { label: "추천업체", value: stats.totalImageSponsors || 0 },
      ],
    },
    {
      title: "회원 관리",
      description: "가입 회원 정보 조회 및 관리",
      icon: Users,
      href: "/admin/users",
      color: "bg-blue-500",
      stats: [
        { label: "총 회원", value: stats.totalUsers },
      ],
    },
    {
      title: "회원 메시지 발송",
      description: "개별/다중/그룹 회원에게 직접 메시지 + 알림 전송",
      icon: MessageSquare,
      href: "/admin/messages",
      color: "bg-purple-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "레거시 회원 임포트",
      description: "LizTV 5,098명 → newcoupling.com 일회성 마이그레이션",
      icon: Database,
      href: "/admin/legacy-import",
      color: "bg-emerald-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "회원 비밀번호 즉시 재설정",
      description: "메일 미수신/잊은 비번 시 운영자가 임시 비번 직접 발급",
      icon: KeyRound,
      href: "/admin/legacy-activate",
      color: "bg-rose-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "회원 일괄 작업",
      description: "프리미엄 → 정회원 일괄 변환 등 대량 회원 정리 도구",
      icon: RefreshCw,
      href: "/admin/bulk-ops",
      color: "bg-amber-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "결제 & 이벤트 관리",
      description: "줌데이트 신청, 멤버십 결제 내역 관리",
      icon: CreditCard,
      href: "/admin/payments",
      color: "bg-green-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "게시판 관리",
      description: "커뮤니티 게시글 및 댓글 관리",
      icon: MessageSquare,
      href: "/admin/posts",
      color: "bg-orange-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "상담 예약 관리",
      description: "상담 예약 내역 및 결제 현황 관리",
      icon: Calendar,
      href: "/admin/counseling",
      color: "bg-teal-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "WP 데이터 마이그레이션",
      description: "WordPress 사용자/게시글 CSV 이관",
      icon: Database,
      href: "/admin/migration",
      color: "bg-violet-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "서류인증 관리",
      description: "혼인관계증명서 등 서류 심사 및 승인",
      icon: FileText,
      href: "/admin/documents",
      color: "bg-cyan-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "사전등록 정회원",
      description: "이메일 사전등록 → 가입 시 자동 정회원 부여",
      icon: UserPlus,
      href: "/admin/pre-register",
      color: "bg-emerald-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "리즈커플 관리",
      description: "커플 게시판에 표시될 성공 커플 사진 및 정보",
      icon: Heart,
      href: "/admin/liz-couples",
      color: "bg-pink-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "회원 찾기",
      description: "키워드로 조건에 맞는 회원 검색 (나이, 거주지, 종교, 성격, 취미)",
      icon: Search,
      href: "/admin/member-search",
      color: "bg-indigo-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "사이드바 광고 관리",
      description: "커뮤니티 사이드바에 노출되는 AD 카드 등록·수정·숨기기·삭제",
      icon: Image,
      href: "/admin/ads",
      color: "bg-amber-500",
      stats: [],
      comingSoon: false,
    },
    {
      title: "💳 PayPal 결제 관리",
      description: "결제 내역 조회 · 이벤트/특별 프로그램 가격 · Legacy 회원 토글",
      icon: DollarSign,
      href: "/admin/paypal",
      color: "bg-emerald-500",
      stats: [],
      comingSoon: false,
    },
  ];

  const quickStats = [
    {
      label: "총 콘텐츠",
      value: stats.totalMannerContents + stats.totalImageContents,
      icon: FileText,
      change: "+12%",
      positive: true,
    },
    {
      label: "총 회원",
      value: stats.totalUsers,
      icon: Users,
      change: "+8%",
      positive: true,
    },
    {
      label: "추천 업체",
      value: (stats.totalMannerSponsors || 0) + (stats.totalImageSponsors || 0),
      icon: Building2,
      change: "+3",
      positive: true,
    },
  ];

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-burgundy-500 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-800">관리자 대시보드</h1>
              <p className="text-stone-500 text-sm">New Coupling 서비스 통합 관리</p>
            </div>
          </div>
        </div>

        {/* 🔔 리즈상담 결제 알림 */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 mb-8 overflow-hidden" data-testid="counseling-notifications">
          <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 bg-gradient-to-r from-burgundy-50/60 to-amber-50/40">
            <div className="flex items-center gap-3">
              <div className="relative">
                {counselingUnread > 0 ? (
                  <BellRing className="w-5 h-5 text-burgundy-500" />
                ) : (
                  <Bell className="w-5 h-5 text-stone-400" />
                )}
                {counselingUnread > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="counseling-unread-badge">
                    {counselingUnread > 99 ? "99+" : counselingUnread}
                  </span>
                )}
              </div>
              <div>
                <h3 className="font-bold text-stone-800">리즈상담 결제 알림</h3>
                <p className="text-xs text-stone-500">
                  결제 완료된 최근 상담 예약 · 미확인 <span className="font-semibold text-burgundy-500">{counselingUnread}</span>건
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {counselingUnread > 0 && (
                <button
                  onClick={markAllCounselingSeen}
                  className="text-xs font-medium text-stone-600 hover:text-burgundy-500 px-3 py-1.5 rounded-lg hover:bg-stone-100 flex items-center gap-1.5 transition-colors"
                  data-testid="mark-all-seen-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  모두 읽음
                </button>
              )}
              <button
                onClick={() => navigate("/admin/counseling")}
                className="text-xs font-medium text-burgundy-500 hover:text-burgundy-600 px-3 py-1.5 rounded-lg hover:bg-burgundy-50 flex items-center gap-1.5 transition-colors"
                data-testid="view-all-counseling-btn"
              >
                전체 보기 <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {counselingLoading ? (
            <div className="px-6 py-10 text-center">
              <Loader2 className="w-6 h-6 text-burgundy-500 mx-auto animate-spin" />
            </div>
          ) : counselingNotifications.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Bell className="w-8 h-8 text-stone-300 mx-auto mb-2" />
              <p className="text-sm text-stone-500">결제 완료된 상담이 아직 없습니다</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100 max-h-[340px] overflow-y-auto">
              {counselingNotifications.map((n) => {
                const isUnread = !n.admin_seen;
                const paidDate = n.paid_at || n.created_at;
                const d = paidDate ? new Date(paidDate) : null;
                const dateStr = d ? `${d.getMonth() + 1}월 ${d.getDate()}일 ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}` : "";
                const methodLabel = n.method === "phone" ? "전화" : n.method === "kakao" ? "카카오톡" : n.method === "zoom" ? "화상" : (n.method || "-");
                const MethodIcon = n.method === "phone" ? Phone : n.method === "kakao" ? MessageCircle : Mail;
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-6 py-3.5 hover:bg-stone-50 transition-colors ${isUnread ? "bg-burgundy-50/30" : ""}`}
                    data-testid={`notif-${n.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isUnread ? "bg-burgundy-500 text-white" : "bg-stone-100 text-stone-400"}`}>
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {isUnread && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
                        <span className="text-sm font-semibold text-stone-800 truncate">
                          {n.username || n.email?.split("@")[0] || "회원"}
                        </span>
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">결제완료</span>
                        <span className="text-xs text-stone-400">{dateStr}</span>
                      </div>
                      <p className="text-sm text-stone-700 mb-1 truncate">
                        <span className="font-medium">{n.service_title}</span>
                        {n.amount ? <span className="text-stone-500 ml-2">· ${n.amount}</span> : null}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-stone-500">
                        <span className="flex items-center gap-1"><MethodIcon className="w-3 h-3" />{methodLabel}</span>
                        {n.phone && <span>☎ {n.phone}</span>}
                        {n.kakao_id && <span>🗨 {n.kakao_id}</span>}
                        <span className="truncate">{n.email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isUnread && (
                        <button
                          onClick={() => markOneSeen(n.id)}
                          className="text-[11px] text-stone-500 hover:text-burgundy-500 px-2 py-1 rounded hover:bg-stone-100 transition-colors"
                          data-testid={`mark-seen-${n.id}`}
                        >읽음</button>
                      )}
                      <button
                        onClick={() => navigate("/admin/counseling")}
                        className="text-[11px] text-burgundy-500 hover:text-burgundy-700 px-2 py-1 rounded hover:bg-burgundy-50 transition-colors"
                      >상세</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {quickStats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-burgundy-100 rounded-xl flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-burgundy-500" />
                </div>
                <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                  stat.positive ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                }`}>
                  {stat.change}
                </span>
              </div>
              <div>
                <p className="text-3xl font-bold text-stone-800">
                  {isLoading ? "-" : stat.value.toLocaleString()}
                </p>
                <p className="text-stone-500 text-sm">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Activity Chart */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-bold text-stone-800">일별 활동 추이</h3>
                <p className="text-sm text-stone-500">최근 7일간 가입자 및 방문자</p>
              </div>
              <div className="flex gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-burgundy-500" />
                  <span className="text-stone-600">가입자</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-gold-400" />
                  <span className="text-stone-600">방문자</span>
                </div>
              </div>
            </div>
            {isLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={stats.dailySignups}>
                  <defs>
                    <linearGradient id="colorSignups" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#722F37" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#722F37" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="name" tick={{ fill: '#78716c', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#78716c', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e5e5',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="방문자" 
                    stroke="#D4AF37" 
                    fillOpacity={1} 
                    fill="url(#colorVisitors)" 
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="가입자" 
                    stroke="#722F37" 
                    fillOpacity={1} 
                    fill="url(#colorSignups)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Content Views Chart */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-bold text-stone-800">인기 콘텐츠</h3>
                <p className="text-sm text-stone-500">조회수 및 좋아요 TOP 6</p>
              </div>
            </div>
            {isLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.contentViews} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis type="number" tick={{ fill: '#78716c', fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" tick={{ fill: '#78716c', fontSize: 11 }} width={80} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e5e5',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Bar dataKey="조회수" fill="#722F37" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="좋아요" fill="#ec4899" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Category Distribution */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-bold text-stone-800">카테고리별 콘텐츠</h3>
                <p className="text-sm text-stone-500">매너 클래스 카테고리 분포</p>
              </div>
            </div>
            {isLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={stats.categoryDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {stats.categoryDistribution.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={[
                            '#722F37', '#D4AF37', '#ec4899', '#8b5cf6', 
                            '#06b6d4', '#10b981', '#f59e0b'
                          ][index % 7]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e5e5',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                      formatter={(value) => [`${value}개`, '콘텐츠']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Monthly Summary */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-bold text-stone-800">이번 달 요약</h3>
                <p className="text-sm text-stone-500">주요 지표 현황</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-burgundy-50 to-white rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-burgundy-100 rounded-lg flex items-center justify-center">
                    <Eye className="w-5 h-5 text-burgundy-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">총 조회수</p>
                    <p className="text-xl font-bold text-stone-800">
                      {isLoading ? '-' : stats.contentViews.reduce((sum, c) => sum + c.조회수, 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-green-600 font-medium">+24%</span>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-pink-50 to-white rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                    <Heart className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">총 좋아요</p>
                    <p className="text-xl font-bold text-stone-800">
                      {isLoading ? '-' : stats.contentViews.reduce((sum, c) => sum + c.좋아요, 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-green-600 font-medium">+18%</span>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gold-50 to-white rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gold-100 rounded-lg flex items-center justify-center">
                    <Crown className="w-5 h-5 text-gold-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">프리미엄 전환율</p>
                    <p className="text-xl font-bold text-stone-800">12.5%</p>
                  </div>
                </div>
                <span className="text-sm text-green-600 font-medium">+3.2%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Modules Grid */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-stone-800 mb-4">관리 메뉴</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {adminModules.map((module, index) => (
              <div
                key={index}
                onClick={() => !module.comingSoon && navigate(module.href)}
                className={`bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-200 transition-all ${
                  module.comingSoon 
                    ? "opacity-60 cursor-not-allowed" 
                    : "hover:shadow-md hover:border-burgundy-200 cursor-pointer"
                }`}
              >
                <div className={`h-2 ${module.color}`} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 ${module.color} rounded-xl flex items-center justify-center`}>
                      <module.icon className="w-6 h-6 text-white" />
                    </div>
                    {module.comingSoon ? (
                      <span className="text-xs bg-stone-200 text-stone-500 px-2 py-1 rounded-full">
                        준비중
                      </span>
                    ) : (
                      <ArrowRight className="w-5 h-5 text-stone-400" />
                    )}
                  </div>
                  
                  <h3 className="font-bold text-stone-800 mb-1">{module.title}</h3>
                  <p className="text-sm text-stone-500 mb-4">{module.description}</p>
                  
                  {module.stats && module.stats.length > 0 && (
                    <div className="flex gap-4 pt-4 border-t border-stone-100">
                      {module.stats.map((stat, statIndex) => (
                        <div key={statIndex}>
                          <p className="text-xl font-bold text-stone-800">
                            {isLoading ? "-" : stat.value}
                          </p>
                          <p className="text-xs text-stone-500">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity (관리자 대시보드 내장) */}
        <AdminActivitySection />
      </div>
    </div>
  );
}
